import {
  Button,
  Icon,
  TextAreaInput,
  TextInput,
  TextOutlinedInput,
  Collapse,
} from '@octano/global-ui';
import { useFieldArray } from 'react-hook-form';
import { Control } from 'react-hook-form/dist/types/form';
import { Col, Row, Container } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';
import { useValidations } from '../../../../../../hooks/useValidations';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../../../locales/translations';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { SelectOptionType } from '@octano/global-ui';
import { SectionForm, SectionKind } from '../../../types/types';

export interface FormValues {
  context: SelectOptionType | null;
  title: string;
  period: SelectOptionType | null;
  scale: SelectOptionType | null;
  startDate: string;
  endDate: string;
  sections: SectionForm[];
}

interface SurveySectionProps {
  id: string;
  control: Control<FormValues>;
  sectionIndex: number;
  kind: SectionKind;
  errors: any;
  onRemove: () => void;
  disabled?: boolean;
}

export default function SurveySection({
  id,
  control,
  sectionIndex,
  kind,
  errors,
  onRemove,
  disabled = false,
}: SurveySectionProps) {
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_SURVEY);
  const { fields: questions, append, remove, move } = useFieldArray({
    control,
    name: `sections.${sectionIndex}.questions`,
  });

  const isOpen = kind === SectionKind.Open;

  const { msgValidations } = useValidations();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = questions.findIndex((q) => q.id === active.id);
      const newIndex = questions.findIndex((q) => q.id === over.id);
      move(oldIndex, newIndex);
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 0,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Collapse
        headerStyles={{ padding: 0 }}
        contentStyles={{ padding: 0 }}
        renderBtn={() => <></>}
        renderTitle={(props) => (
          <Container fluid>
            <Row>
              <Col xs={12} md={8}>
                <TextInput
                  label={t('surveyCreation.sectionFields.title.label')}
                  name={`sections.${sectionIndex}.title`}
                  control={control}
                  placeholder={t(
                    'surveyCreation.sectionFields.title.placeholder',
                  )}
                  rules={{ required: msgValidations.required }}
                  readOnly={disabled}
                />
              </Col>
              <Col xs={12} md={2}>
                <div className="h-100 pt-3 d-flex" style={{ gap: '20px' }}>
                  {!disabled && (
                    <>
                      <span
                        className="text-muted mr-3"
                        {...listeners}
                        style={{ cursor: 'grab' }}
                      >
                        <Icon name="drag_dots" size={20} />
                      </span>
                      <span className="text-muted" onClick={onRemove}>
                        <Icon name="trash" size={20} />
                      </span>
                    </>
                  )}
                  <span className="text-muted ml-3" onClick={props.toggle}>
                    <Icon
                      name={props.isOpened ? 'chevron_up' : 'chevron_down'}
                      size={20}
                    />
                  </span>
                </div>
              </Col>

              <Col>
                {errors.sections?.[sectionIndex]?.questions && (
                  <p style={{ color: 'red' }}>
                    {errors.sections[sectionIndex].questions.message}
                  </p>
                )}
              </Col>
            </Row>
          </Container>
        )}
      >
        <Col xs={12} md={8}>
          <TextAreaInput
            label=""
            name={`sections.${sectionIndex}.description`}
            control={control}
            placeholder={t(
              'surveyCreation.sectionFields.description.placeholder',
            )}
            readOnly={disabled}
          />
        </Col>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={questions.map((q) => q.id)}
            strategy={verticalListSortingStrategy}
          >
            {questions.map((question, questionIndex) => (
              <SortableQuestion
                key={question.id}
                id={question.id}
                questionIndex={questionIndex}
                sectionIndex={sectionIndex}
                control={control}
                errors={errors}
                onRemove={() => remove(questionIndex)}
                disabled={disabled}
                isOpen={isOpen}
              />
            ))}
          </SortableContext>
        </DndContext>

        {!disabled && (
          <Col xs={12} md={8} className="pl-1 mt-3">
            <Button
              outlined
              color="primary"
              size="md"
              onClick={() => append({ id: uuidv4(), text: '' })}
              icon="plus"
              fullwidth
              text={t(
                isOpen
                  ? 'surveyCreation.questionActions.addOpenQuestion'
                  : 'surveyCreation.questionActions.addQuestion',
              )}
              className="mb-3"
            />
          </Col>
        )}
      </Collapse>
    </div>
  );
}

const SortableQuestion = ({
  id,
  questionIndex,
  sectionIndex,
  control,
  errors,
  onRemove,
  disabled = false,
  isOpen = false,
}: {
  id: string;
  questionIndex: number;
  sectionIndex: number;
  control: Control<FormValues>;
  errors: any;
  onRemove: () => void;
  disabled?: boolean;
  isOpen?: boolean;
}) => {
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_SURVEY);
  const { msgValidations } = useValidations();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 0,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Container fluid>
        <Row>
          <Col xs={12} md={8}>
            <div className="w-full d-flex mt-3">
              <span
                {...listeners}
                style={{ cursor: 'grab', marginRight: '8px', marginTop: '8px' }}
              >
                {!disabled && <Icon name="drag_dots" size={20} />}
              </span>
              <span className="mt-2" style={{ width: '3%' }}>
                {questionIndex + 1}.
              </span>
              <div style={{ width: '100%' }}>
                <TextOutlinedInput
                  name={`sections.${sectionIndex}.questions.${questionIndex}.text`}
                  control={control}
                  placeholder={`${t(
                    isOpen
                      ? 'surveyCreation.openQuestionFields.placeholder'
                      : 'surveyCreation.questionFields.placeholder',
                  )} ${questionIndex + 1}...`}
                  rules={{ required: msgValidations.required }}
                  readOnly={disabled}
                />
              </div>
            </div>
          </Col>
          {!disabled && (
            <Col xs={12} md={2} className="pt-4">
              <span className="text-muted" onClick={onRemove}>
                <Icon name="trash" size={20} color="primary" />
              </span>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  );
};
